import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiDownload, FiEye, FiFile, FiFileText, FiImage, FiSearch, FiX } from "react-icons/fi";
import { client } from "../../Clients";
import type { MediaMonthFileListingDto } from "./backupClient";
import { DataTable, Pill, extractApiError, formatBytes } from "./ui";

/**
 * Navigateur de sauvegarde médias pour UN mois : le téléchargement du zip mensuel complet a
 * été retiré de l'API (410 : vecteur d'exfiltration massive et timeouts de passerelle) ; ce
 * panneau le remplace par la récupération chirurgicale, fichier par fichier :
 * - listing paginé côté serveur (GET months/{mois}/files?skip=&take=), `total` avant pagination ;
 * - badge « mois archivé (zip) » (entrées du zip roté) vs « mois courant » (miroir d'objets),
 *   d'après `source` renvoyé par l'API ;
 * - recherche CLIENT sur la page chargée uniquement (le filtre ne re-interroge pas le serveur) ;
 * - téléchargement unitaire via axios en blob (le Bearer part par l'intercepteur : un lien
 *   direct serait non authentifié sur les instances déployées), puis enregistrement navigateur.
 * Endpoints en lecture (feature:backup:read) : aucun gating d'écriture ici.
 */

/** Étiquette de type courte dérivée de l'extension (l'API ne renvoie pas de contentType au listing). */
const fileTypeLabel = (key: string, contentType?: string | null): string => {
	if (contentType) return contentType;
	const name = key.split("/").pop() ?? key;
	const dot = name.lastIndexOf(".");
	return dot > 0 ? name.slice(dot + 1).toUpperCase() : "-";
};

/** Famille visuelle du fichier, déduite de l'extension (le listing ne porte pas de contentType). */
type FileKind = "image" | "pdf" | "other";
const fileKind = (key: string): FileKind => {
	const ext = (key.split(".").pop() ?? "").toLowerCase();
	if (["png", "jpg", "jpeg", "gif", "webp", "svg", "avif", "bmp", "ico"].includes(ext)) return "image";
	if (ext === "pdf") return "pdf";
	return "other";
};
const KIND_ICONS: Record<FileKind, React.ComponentType<{ className?: string }>> = {
	image: FiImage,
	pdf: FiFileText,
	other: FiFile,
};

/**
 * Visualiseur d'un fichier de sauvegarde : le contenu est récupéré en blob AUTHENTIFIÉ (un <img>
 * pointant l'URL de l'API partirait sans Bearer sur les instances déployées) puis affiché via un
 * object URL : image plein cadre ou PDF en iframe : avec navigation précédent/suivant parmi les
 * fichiers prévisualisables de la page chargée.
 */
const FilePreviewOverlay: React.FC<{
	month: string;
	files: Array<{ key: string; size: number }>;
	index: number;
	onNavigate: (index: number) => void;
	onClose: () => void;
}> = ({ month, files, index, onNavigate, onClose }) => {
	const file = files[index];
	const kind = fileKind(file.key);
	const [objectUrl, setObjectUrl] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let alive = true;
		let url: string | null = null;
		setObjectUrl(null);
		setError(null);
		(async () => {
			try {
				const blob = await client.backup.fetchMediaMonthFileBlob(month, file.key);
				if (!alive) return;
				// Le blob de l'API arrive parfois en application/octet-stream : retype pour l'iframe PDF.
				const typed = kind === "pdf" && blob.type !== "application/pdf" ? new Blob([blob], { type: "application/pdf" }) : blob;
				url = URL.createObjectURL(typed);
				setObjectUrl(url);
			} catch (e) {
				if (alive) setError(extractApiError(e));
			}
		})();
		return () => {
			alive = false;
			if (url) URL.revokeObjectURL(url);
		};
	}, [month, file.key, kind]);

	return (
		<div
			className="fixed inset-0 z-[1400] flex flex-col bg-slate-900/80 p-4"
			role="dialog"
			aria-modal="true"
			aria-label={`Aperçu de ${file.key}`}
			onClick={onClose}
		>
			<div className="flex items-center justify-between gap-3 text-white" onClick={(e) => e.stopPropagation()}>
				<p className="min-w-0 truncate text-sm font-medium" title={file.key}>
					{file.key} <span className="text-white/60">· {formatBytes(file.size)}</span>
				</p>
				<div className="flex shrink-0 items-center gap-2">
					<button
						type="button"
						disabled={index === 0}
						onClick={() => onNavigate(index - 1)}
						className="rounded-lg bg-white/10 p-2 hover:bg-white/20 disabled:opacity-40"
						title="Fichier précédent"
					>
						<FiChevronLeft className="h-4 w-4" aria-hidden />
					</button>
					<span className="text-xs text-white/70">
						{index + 1} / {files.length}
					</span>
					<button
						type="button"
						disabled={index + 1 >= files.length}
						onClick={() => onNavigate(index + 1)}
						className="rounded-lg bg-white/10 p-2 hover:bg-white/20 disabled:opacity-40"
						title="Fichier suivant"
					>
						<FiChevronRight className="h-4 w-4" aria-hidden />
					</button>
					<button
						type="button"
						onClick={() => client.backup.downloadMediaMonthFile(month, file.key)}
						className="rounded-lg bg-white/10 p-2 hover:bg-white/20"
						title="Télécharger ce fichier"
					>
						<FiDownload className="h-4 w-4" aria-hidden />
					</button>
					<button type="button" onClick={onClose} className="rounded-lg bg-white/10 p-2 hover:bg-white/20" title="Fermer l'aperçu">
						<FiX className="h-4 w-4" aria-hidden />
					</button>
				</div>
			</div>
			<div className="mt-3 flex min-h-0 flex-1 items-center justify-center" onClick={(e) => e.stopPropagation()}>
				{error ? (
					<p className="rounded-lg bg-white px-4 py-3 text-sm text-red-700">{error}</p>
				) : !objectUrl ? (
					<p className="text-sm text-white/70">Chargement de l'aperçu…</p>
				) : kind === "image" ? (
					<img src={objectUrl} alt={file.key} className="max-h-full max-w-full rounded-lg bg-white object-contain shadow-2xl" />
				) : (
					<iframe src={`${objectUrl}#view=FitH`} title={`Aperçu de ${file.key}`} className="h-full w-full max-w-4xl rounded-lg bg-white shadow-2xl" />
				)}
			</div>
		</div>
	);
};

interface MediaMonthBrowserProps {
	month: string;
	onClose: () => void;
	/** Taille de page serveur (take) : surchargée par les tests. */
	pageSize?: number;
}

const MediaMonthBrowser: React.FC<MediaMonthBrowserProps> = ({ month, onClose, pageSize = 100 }) => {
	const [listing, setListing] = useState<MediaMonthFileListingDto | null>(null);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [filter, setFilter] = useState("");
	const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
	/** Index (dans la liste des fichiers prévisualisables affichés) du fichier en aperçu, ou null. */
	const [previewIndex, setPreviewIndex] = useState<number | null>(null);

	useEffect(() => {
		let alive = true;
		setLoading(true);
		(async () => {
			try {
				const l = await client.backup.listMediaMonthFiles(month, { skip: page * pageSize, take: pageSize });
				if (alive) {
					setListing(l);
					setError(null);
				}
			} catch (e) {
				// 404 (mois sans sauvegarde), 410 (route retirée) et pannes : message de l'API tel quel.
				if (alive) setError(extractApiError(e));
			} finally {
				if (alive) setLoading(false);
			}
		})();
		return () => {
			alive = false;
		};
	}, [month, page, pageSize]);

	const download = async (key: string) => {
		setDownloadingKey(key);
		try {
			await client.backup.downloadMediaMonthFile(month, key);
		} catch (e) {
			setError(extractApiError(e));
		} finally {
			setDownloadingKey(null);
		}
	};

	const normalizedFilter = filter.trim().toLowerCase();
	const visibleFiles = useMemo(
		() => (listing?.files ?? []).filter((f) => !normalizedFilter || f.key.toLowerCase().includes(normalizedFilter)),
		[listing, normalizedFilter]
	);
	// Fichiers ouvrables dans le visualiseur (images + PDF) parmi la page affichée.
	const previewableFiles = useMemo(() => visibleFiles.filter((f) => fileKind(f.key) !== "other"), [visibleFiles]);
	const openPreview = useCallback(
		(key: string) => {
			const idx = previewableFiles.findIndex((f) => f.key === key);
			if (idx >= 0) setPreviewIndex(idx);
		},
		[previewableFiles]
	);

	const total = listing?.total ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / pageSize));
	const from = total === 0 ? 0 : page * pageSize + 1;
	const to = Math.min(total, page * pageSize + (listing?.files.length ?? 0));

	return (
		<div
			className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/50 p-4"
			role="dialog"
			aria-modal="true"
			aria-label={`Fichiers de la sauvegarde médias ${month}`}
		>
			<div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
				<div className="flex items-start justify-between gap-3">
					<div className="flex flex-wrap items-center gap-2">
						<h3 className="text-base font-semibold text-slate-800">Sauvegarde médias : {month}</h3>
						{listing &&
							(listing.source === "archive" ? (
								<Pill label="mois archivé (zip)" tone="green" />
							) : (
								<Pill label="mois courant" tone="sky" />
							))}
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label="Fermer"
						className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
					>
						<FiX className="h-5 w-5" aria-hidden />
					</button>
				</div>

				<p className="mt-1 text-xs text-slate-500">
					Téléchargement fichier par fichier (le zip mensuel complet n'est plus téléchargeable depuis l'admin).
				</p>

				{error && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

				<label className="relative mt-3 block">
					<FiSearch className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
					<input
						type="search"
						value={filter}
						onChange={(e) => setFilter(e.target.value)}
						placeholder="Filtrer les fichiers affichés…"
						aria-label="Filtrer les fichiers affichés"
						className="w-full rounded-lg border border-slate-200 py-2 pl-8 pr-3 text-sm focus:border-sky-400 focus:outline-none"
					/>
				</label>
				{normalizedFilter.length > 0 && (
					<p className="mt-1 text-[11px] text-slate-500">
						Filtre appliqué à la page chargée uniquement ({visibleFiles.length}/{listing?.files.length ?? 0} fichier(s) affiché(s)).
					</p>
				)}

				<div className="mt-3 min-h-0 flex-1 overflow-y-auto">
					{loading && !listing ? (
						<p className="px-2 py-4 text-sm italic text-slate-500">Chargement des fichiers…</p>
					) : (
						<DataTable
							headers={["Fichier", "Taille", "Type", ""]}
							rows={visibleFiles.map((f) => {
								const kind = fileKind(f.key);
								const KindIcon = KIND_ICONS[kind];
								return {
									key: f.key,
									cells: [
										<span className="flex min-w-0 items-center gap-2">
											<KindIcon className={`h-4 w-4 shrink-0 ${kind === "image" ? "text-sky-500" : kind === "pdf" ? "text-rose-500" : "text-slate-400"}`} aria-hidden />
											<code className="break-all text-[11px]">{f.key}</code>
										</span>,
										<span className="whitespace-nowrap">{formatBytes(f.size)}</span>,
										fileTypeLabel(f.key, f.contentType),
										<span className="flex items-center justify-end gap-1.5">
											{kind !== "other" && (
												<button
													type="button"
													onClick={() => openPreview(f.key)}
													title={`Aperçu de ${f.key}`}
													className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-sky-200 bg-white px-2.5 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-50"
												>
													<FiEye className="h-3.5 w-3.5" aria-hidden />
													Aperçu
												</button>
											)}
											<button
												type="button"
												disabled={downloadingKey !== null}
												onClick={() => download(f.key)}
												title={`Télécharge ${f.key}`}
												className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
											>
												<FiDownload className="h-3.5 w-3.5" aria-hidden />
												{downloadingKey === f.key ? "…" : "Télécharger"}
											</button>
										</span>,
									],
								};
							})}
							emptyLabel={
								normalizedFilter
									? "Aucun fichier de la page chargée ne correspond au filtre."
									: "Aucun fichier dans cette sauvegarde."
							}
						/>
					)}
				</div>

				<div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
					<p className="text-xs text-slate-600">
						{total === 0 ? "0 fichier" : `${from}-${to} sur ${total} fichier(s)`}
						{loading && listing ? " : chargement…" : ""}
					</p>
					<div className="flex items-center gap-2">
						<button
							type="button"
							disabled={page === 0 || loading}
							onClick={() => setPage((p) => Math.max(0, p - 1))}
							className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
						>
							<FiChevronLeft className="h-3.5 w-3.5" aria-hidden />
							Précédent
						</button>
						<span className="text-xs text-slate-500">
							page {page + 1} / {pageCount}
						</span>
						<button
							type="button"
							disabled={page + 1 >= pageCount || loading}
							onClick={() => setPage((p) => p + 1)}
							className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
						>
							Suivant
							<FiChevronRight className="h-3.5 w-3.5" aria-hidden />
						</button>
					</div>
				</div>
			</div>

			{previewIndex !== null && previewableFiles[previewIndex] && (
				<FilePreviewOverlay
					month={month}
					files={previewableFiles}
					index={previewIndex}
					onNavigate={setPreviewIndex}
					onClose={() => setPreviewIndex(null)}
				/>
			)}
		</div>
	);
};

export default MediaMonthBrowser;
